import React from 'react';
import { motion } from 'framer-motion';
import { PowerUpState, PowerUp } from '../../types';
import { getAvailablePowerUps } from '../../data/powerUps';
import audioFileService from '../../services/audioFileService';

interface PowerUpsPanelProps {
  powerUps: PowerUpState;
  lives: number;
  onUsePowerUp: (powerUpId: string) => void;
  disabled?: boolean;
}

export const PowerUpsPanel: React.FC<PowerUpsPanelProps> = ({
  powerUps,
  lives,
  onUsePowerUp,
  disabled = false
}) => {
  const availablePowerUps = getAvailablePowerUps(powerUps);

  const handlePowerUpClick = (powerUp: PowerUp) => {
    if (disabled || powerUp.used) return;
    
    audioFileService.play('click');
    onUsePowerUp(powerUp.id);
  };

  const renderLives = () => {
    return (
      <div className="flex items-center gap-2">
        <span className="text-2xl">💖</span>
        <span className="text-lg font-bold text-red-600">{lives}</span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
      {/* Lives Display */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-banana-800">⚡ Power-Ups Mode</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Lives:</span>
          {renderLives()}
        </div>
      </div>

      {/* Power-ups Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.values(powerUps).map((powerUp) => (
          <motion.button
            key={powerUp.id}
            onClick={() => handlePowerUpClick(powerUp)}
            disabled={disabled || powerUp.used}
            className={`p-3 rounded-lg border-2 text-center transition-all ${
              powerUp.used
                ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                : disabled
                ? 'border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed'
                : 'border-banana-300 bg-banana-50 hover:border-banana-500 hover:bg-banana-100 text-banana-800 cursor-pointer'
            }`}
            whileHover={!powerUp.used && !disabled ? { scale: 1.05 } : {}}
            whileTap={!powerUp.used && !disabled ? { scale: 0.95 } : {}}
          >
            <div className="text-2xl mb-1">{powerUp.icon}</div>
            <div className="text-xs font-semibold mb-1">{powerUp.name}</div>
            <div className="text-xs text-gray-600">{powerUp.description}</div>
            {powerUp.used && (
              <div className="text-xs text-red-500 font-medium mt-1">USED</div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Available Power-ups Counter */}
      <div className="mt-4 text-center">
        <span className="text-sm text-gray-600">
          {availablePowerUps.length} power-ups remaining
        </span>
      </div>
    </div>
  );
}; 